import { useCallback, useEffect, useRef, useState } from "react";

type Direction = "next" | "prev";

interface PageState {
  cursor: string | null;
  direction: Direction;
}

interface PaginatedResponse<T> {
  data: T[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface SortState {
  column: string;
  direction: "asc" | "desc";
}

interface UseCursorPaginationOptions {
  endpoint: string;
  limit?: number;
  filters?: Record<string, string>;
  /** Server-side sort. When set, sent as `?sort=<column>.<direction>`. */
  sort?: SortState | null;
  /** Bumping this re-fetches the current page without resetting it. */
  refreshKey?: number;
  /** When false, suppress fetching (e.g. while waiting for required data). */
  enabled?: boolean;
}

interface UseCursorPaginationResult<T> {
  data: T[];
  loading: boolean;
  total: number;
  page: number;
  hasNext: boolean;
  hasPrev: boolean;
  startRow: number;
  endRow: number;
  handleNext: () => void;
  handlePrev: () => void;
  searchInput: string;
  handleSearchInput: (value: string) => void;
  /** Reset to page 1 — useful when the sort or another non-filter input changes. */
  resetToFirstPage: () => void;
}

const DEFAULT_LIMIT = 50;
const DEBOUNCE_MS = 300;

export function useCursorPagination<T>(
  options: UseCursorPaginationOptions,
): UseCursorPaginationResult<T> {
  const {
    endpoint,
    limit = DEFAULT_LIMIT,
    filters = {},
    sort = null,
    refreshKey = 0,
    enabled = true,
  } = options;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [prevCursor, setPrevCursor] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [pageRequest, setPageRequest] = useState<PageState>({
    cursor: null,
    direction: "next",
  });

  const [searchInput, setSearchInput] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filtersKey = JSON.stringify(filters);
  const sortKey = sort ? `${sort.column}.${sort.direction}` : "";

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    if (pageRequest.cursor) {
      params.set("cursor", pageRequest.cursor);
      params.set("direction", pageRequest.direction);
    }
    if (search) params.set("search", search);
    if (sortKey) params.set("sort", sortKey);

    const parsed = JSON.parse(filtersKey) as Record<string, string>;
    for (const [key, value] of Object.entries(parsed)) {
      if (value) params.set(key, value);
    }

    try {
      const res = await fetch(`${endpoint}?${params}`);
      const json: PaginatedResponse<T> = await res.json();
      setData(json.data);
      setNextCursor(json.nextCursor);
      setPrevCursor(json.prevCursor);
      setHasNext(json.hasNext);
      setHasPrev(json.hasPrev);
      setTotal(json.total);
    } finally {
      setLoading(false);
    }
  }, [endpoint, limit, pageRequest, search, filtersKey, sortKey, enabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey is intentional — bumping it forces a refetch of the current page without resetting it.
  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Reset to page 1 when filters or sort change.
  const resetSignalRef = useRef(`${filtersKey}|${sortKey}`);
  useEffect(() => {
    const next = `${filtersKey}|${sortKey}`;
    if (resetSignalRef.current !== next) {
      resetSignalRef.current = next;
      setPage(1);
      setPageRequest({ cursor: null, direction: "next" });
    }
  }, [filtersKey, sortKey]);

  function handleSearchInput(value: string) {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
      setPageRequest({ cursor: null, direction: "next" });
    }, DEBOUNCE_MS);
  }

  function handleNext() {
    if (nextCursor) {
      setPage((p) => p + 1);
      setPageRequest({ cursor: nextCursor, direction: "next" });
    }
  }

  function handlePrev() {
    if (prevCursor) {
      setPage((p) => Math.max(1, p - 1));
      setPageRequest({ cursor: prevCursor, direction: "prev" });
    }
  }

  function resetToFirstPage() {
    setPage(1);
    setPageRequest({ cursor: null, direction: "next" });
  }

  const startRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const endRow = Math.min(page * limit, total);

  return {
    data,
    loading,
    total,
    page,
    hasNext,
    hasPrev,
    startRow,
    endRow,
    handleNext,
    handlePrev,
    searchInput,
    handleSearchInput,
    resetToFirstPage,
  };
}
